/**
 * Weekly Performance Bar Component
 * Displays irrigation progress vs plan with special scaling logic
 */

const React = require('react');
const { useTranslation } = require('react-i18next');
const PropTypes = require('prop-types');
require('./WeeklyPerformanceBar.css');

/**
 * WeeklyPerformanceBar component
 * @param {Object} props
 * @param {number} props.actualValue - Actual irrigation amount
 * @param {number} props.plannedValue - Planned irrigation amount (7 * dailyReturn)
 * @param {number} props.totalBarWidth - Total bar width value
 * @param {number} props.percentage - Fill percentage (0-100)
 * @param {number} props.plannedPosition - Planned marker position percentage (default 80)
 * @param {string} props.unit - Unit of measurement
 */
const WeeklyPerformanceBar = ({
  actualValue,
  plannedValue,
  totalBarWidth,
  percentage,
  plannedPosition,
  unit,
}) => {
  const { t } = useTranslation();
  const hasData = actualValue !== undefined && totalBarWidth > 0;

  if (!hasData) {
    return (
      <div className='weekly-performance-bar weekly-performance-bar--empty'>
        <span
          className='text-style-short-lables-14-regular'
          style={{ color: 'var(--color-grey-71)' }}
        >
          {t('no_data')}
        </span>
      </div>
    );
  }

  const isHours = unit === 'h';
  const isInch = unit === '\u2033';
  const formatVal = (v) =>
    isHours ? Math.round(v * 2) / 2 : parseFloat(v.toFixed(isInch ? 2 : 1));
  const displayActual = formatVal(actualValue);
  const displayPlanned = plannedValue > 0 ? formatVal(plannedValue) : null;

  return (
    <div className='weekly-performance-bar'>
      <div className='weekly-performance-bar__labels'>
        <span
          className='text-style-short-lables-12-semi-bold'
          style={{ color: 'var(--color-grey-45)' }}
        >
          {t('weekly_performance')}
        </span>
        <span
          className='text-style-short-lables-12-regular'
          style={{ color: 'var(--color-grey-71)' }}
        >
          {displayActual} {unit}
          {displayPlanned && ` / ${displayPlanned} ${unit}`}
        </span>
      </div>

      <div className='weekly-performance-bar__container'>
        <div className='weekly-performance-bar__background'>
          {/* Filled portion */}
          <div
            className='weekly-performance-bar__fill'
            style={{ width: `${percentage}%` }}
          />

          {/* Planned marker at 75% position */}
          {plannedValue > 0 && (
            <div
              className='weekly-performance-bar__planned-marker'
              style={{ left: `${plannedPosition}%` }}
            >
              <div className='weekly-performance-bar__planned-line' />
            </div>
          )}
        </div>
      </div>

      {/* Value labels below bar */}
      <div className='weekly-performance-bar__value-labels'>
        <span
          className='text-style-short-lables-12-regular'
          style={{ color: 'var(--color-grey-71)' }}
        >
          0
        </span>
        {displayPlanned && (
          <span
            className='text-style-short-lables-12-regular weekly-performance-bar__planned-label'
            style={{
              color: 'var(--color-primary-idel)',
              left: `${plannedPosition}%`,
            }}
          >
            {t('plan')}
          </span>
        )}
        <span
          className='text-style-short-lables-12-regular'
          style={{ color: 'var(--color-grey-71)' }}
        >
          {totalBarWidth.toFixed(0)}
        </span>
      </div>
    </div>
  );
};

WeeklyPerformanceBar.propTypes = {
  actualValue: PropTypes.number.isRequired,
  plannedValue: PropTypes.number,
  totalBarWidth: PropTypes.number.isRequired,
  percentage: PropTypes.number.isRequired,
  plannedPosition: PropTypes.number,
  unit: PropTypes.string.isRequired,
};

WeeklyPerformanceBar.defaultProps = {
  plannedValue: 0,
  plannedPosition: 75,
};

module.exports = WeeklyPerformanceBar;
